import React, { useEffect, useState } from "react";
import { X, ExternalLink, Mic, MicOff } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useVoiceRecorder } from "../lib/useVoiceRecorder";

const KINDS = ["NEW TASK", "IDEA", "FOLLOW-UP", "PROMISE I MADE", "WAITING ON SOMEONE"];

const CaptureModal = ({ open, onClose }) => {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("NEW TASK");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pushTodoist, setPushTodoist] = useState(true);
  const [todoistConnected, setTodoistConnected] = useState(false);
  const [projects, setProjects] = useState([]);
  const [routing, setRouting] = useState({ default_project_id: "", business_map: {} });
  const [projectOverride, setProjectOverride] = useState(""); // "" means auto-route

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setText("");
    setKind("NEW TASK");
    setProjectOverride("");
    (async () => {
      try {
        const s = await api.todoistStatus();
        setTodoistConnected(!!s.connected);
        if (s.connected) {
          const [p, r] = await Promise.all([api.todoistProjects(), api.todoistRouting()]);
          setProjects(p);
          setRouting({
            default_project_id: r.default_project_id || "",
            business_map: r.business_map || {},
          });
        }
      } catch {
        setTodoistConnected(false);
      }
    })();
  }, [open]);

  const handleVoiceBlob = async (blob) => {
    setSaving(true);
    try {
      const push = pushTodoist && todoistConnected && kind === "NEW TASK";
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
      fd.append("audio", blob, `capture.${ext}`);
      fd.append("kind", kind);
      fd.append("push_todoist", push ? "true" : "false");
      if (push && projectOverride) fd.append("project_id", projectOverride);

      const cap = await api.voiceCapture(fd);
      setResult(cap);
      if (push && cap.todoist?.id) {
        toast("Voice → Todoist", { description: cap.transcript?.slice(0, 60) });
      } else if (push && cap.todoist?.error) {
        toast("Voice captured (Todoist skipped)", { description: cap.todoist.error });
      } else {
        toast("Voice captured", { description: cap.transcript?.slice(0, 60) });
      }
    } catch (e) {
      toast("Voice capture failed", { description: e?.response?.data?.detail || "Try again" });
    } finally {
      setSaving(false);
    }
  };

  const voice = useVoiceRecorder(handleVoiceBlob);

  if (!open) return null;

  const projectNameFor = (id) => {
    if (!id) return "AUTO — DEFAULT";
    const p = projects.find((x) => x.id === id);
    return p ? (p.is_inbox_project ? "Inbox" : p.name) : id;
  };

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const push = pushTodoist && todoistConnected && kind === "NEW TASK";
      const cap = await api.createCapture({
        raw_text: text,
        kind,
        push_todoist: push,
        project_id: push && projectOverride ? projectOverride : undefined,
      });
      setResult(cap);
      if (push && cap.todoist?.id) {
        toast("Captured → Todoist", { description: `Landed in ${projectNameFor(cap.todoist.project_id)}` });
      } else if (push && cap.todoist?.error) {
        toast("Captured (Todoist skipped)", { description: cap.todoist.error });
      } else {
        toast("Captured", { description: `Classified as ${cap.kind}` });
      }
    } catch {
      toast("Capture failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setText("");
    setKind("NEW TASK");
    setResult(null);
    setProjectOverride("");
    onClose();
  };

  const canPush = todoistConnected && kind === "NEW TASK";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/70 backdrop-blur-sm p-4 md:p-8 overflow-y-auto"
      onClick={reset}
      data-testid="capture-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="op-panel op-panel--glow w-full max-w-2xl mt-8 md:mt-0"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.05]">
          <div className="flex items-center gap-3">
            <span className="op-ghost-number text-3xl">+</span>
            <div>
              <div className="op-microcopy">INBOX / CAPTURE</div>
              <div className="text-white text-lg tracking-tight">Get it out of your head.</div>
            </div>
          </div>
          <button onClick={reset} data-testid="capture-close" className="text-[#5E657D] hover:text-white">
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <div className="p-6 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="op-microcopy">THOUGHT</div>
                <div className="op-tag">TYPE OR HOLD MIC</div>
              </div>
              <textarea
                autoFocus
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder='e.g., "Call Michael about accident marketing company next week."'
                className="op-input resize-none"
                data-testid="capture-text"
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  type="button"
                  onMouseDown={voice.start}
                  onMouseUp={voice.stop}
                  onMouseLeave={() => { if (voice.state === "recording") voice.stop(); }}
                  onTouchStart={(e) => { e.preventDefault(); voice.start(); }}
                  onTouchEnd={(e) => { e.preventDefault(); voice.stop(); }}
                  disabled={saving}
                  data-testid="voice-record-btn"
                  data-recording={voice.state === "recording"}
                  className={`inline-flex items-center gap-2 px-4 py-2 border transition-all select-none ${
                    voice.state === "recording"
                      ? "border-[#ef4444] text-[#ef4444] bg-[rgba(239,68,68,0.08)] op-p1-flash"
                      : voice.state === "error"
                      ? "border-[#F5A623]/50 text-[#F5A623]"
                      : "border-white/15 text-white hover:border-[#00E5FF]/60 hover:text-[#00E5FF]"
                  }`}
                >
                  {voice.state === "error" ? <MicOff size={13} /> : <Mic size={13} />}
                  <span className="op-mono text-[10px] tracking-[0.24em]">
                    {voice.state === "recording"
                      ? `RECORDING · ${String(voice.elapsed).padStart(2, "0")}s · RELEASE TO SEND`
                      : voice.state === "requesting"
                      ? "REQUESTING MIC…"
                      : voice.state === "error"
                      ? "MIC UNAVAILABLE"
                      : "HOLD TO RECORD"}
                  </span>
                </button>
                {voice.state === "recording" && (
                  <button
                    type="button"
                    onClick={voice.cancel}
                    className="op-tag hover:text-[#F5A623]"
                    data-testid="voice-cancel"
                  >
                    CANCEL
                  </button>
                )}
                {saving && voice.state !== "recording" && (
                  <span className="op-tag op-flicker text-[#00E5FF]">TRANSCRIBING…</span>
                )}
              </div>
              {voice.error && voice.state === "error" && (
                <div className="op-tag mt-2 text-[#F5A623]" data-testid="voice-error">
                  {voice.error}
                </div>
              )}
            </div>
            <div>
              <div className="op-microcopy mb-2">TYPE</div>
              <div className="flex flex-wrap gap-2">
                {KINDS.map((k) => (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    data-testid={`capture-kind-${k.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`op-mono text-[10px] tracking-[0.24em] px-3 py-2 border transition-colors duration-200 ${
                      kind === k
                        ? "border-[#00E5FF] text-[#00E5FF] bg-[rgba(0,229,255,0.06)]"
                        : "border-white/10 text-[#A0A5B5] hover:text-white hover:border-white/25"
                    }`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>

            {canPush && (
              <div>
                <div className="op-microcopy mb-2">TODOIST PROJECT</div>
                <select
                  value={projectOverride}
                  onChange={(e) => setProjectOverride(e.target.value)}
                  className="op-input op-mono text-xs py-2"
                  data-testid="capture-project"
                >
                  <option value="">AUTO — DETECT FROM BUSINESS TAG</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.is_inbox_project ? "Inbox" : p.name}
                    </option>
                  ))}
                </select>
                <div className="op-tag mt-2">
                  AUTO ROUTES BY BUSINESS · DEFAULT: {routing.default_project_id ? projectNameFor(routing.default_project_id) : "INBOX"}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <label
                className={`flex items-center gap-3 cursor-pointer ${canPush ? "opacity-100" : "opacity-40"}`}
                data-testid="capture-push-todoist"
              >
                <span
                  className="op-check"
                  data-checked={pushTodoist && canPush}
                  onClick={(e) => { e.preventDefault(); if (canPush) setPushTodoist(!pushTodoist); }}
                />
                <span className="op-tag">{todoistConnected ? "PUSH TO TODOIST" : "TODOIST NOT CONNECTED"}</span>
              </label>
              <button
                onClick={submit}
                disabled={saving || !text.trim()}
                data-testid="capture-submit"
                className="op-btn op-btn--primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? "CAPTURING…" : "CAPTURE"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-5" data-testid="capture-result">
            <div className="flex items-center justify-between">
              <div className="op-microcopy">CAPTURED · CLASSIFIED</div>
              {result.todoist?.id && result.todoist?.url && (
                <a
                  href={result.todoist.url}
                  target="_blank"
                  rel="noreferrer"
                  className="op-tag text-[#00E5FF] inline-flex items-center gap-1.5"
                  data-testid="capture-todoist-link"
                >
                  VIEW IN TODOIST <ExternalLink size={10} />
                </a>
              )}
            </div>
            <div className="text-white text-lg leading-snug">{result.raw_text}</div>
            {result.source === "voice" && result.transcript && (
              <div className="op-tag" data-testid="capture-source-voice">SOURCE · VOICE · WHISPER</div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                ["TYPE", result.kind],
                ["BUSINESS", result.business || "—"],
                ["PRIORITY", result.priority],
                ["PERSON", result.person || "—"],
                ["PROJECT", result.project || "—"],
                ["DUE", result.due_date || "—"],
              ].map(([k, v]) => (
                <div key={k} className="border border-white/[0.06] p-3">
                  <div className="op-tag mb-1">{k}</div>
                  <div className="text-white text-sm">{v}</div>
                </div>
              ))}
            </div>
            {result.todoist?.error && (
              <div className="text-[#F5A623] op-mono text-xs" data-testid="capture-todoist-error">
                Todoist: {result.todoist.error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setResult(null)} className="op-btn" data-testid="capture-another">
                CAPTURE ANOTHER
              </button>
              <button onClick={reset} className="op-btn op-btn--primary" data-testid="capture-done">
                DONE
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaptureModal;
